/**
 * Anchor Plan — допоміжні функції (Sanctum, URL, object utils, fetch interceptor).
 */

import { APP_CONFIG } from "@/config/app.config";
import { authService } from "@/services/auth.service";
import i18n, { apiLanguage } from "@/i18n";

/* ── API base (узгоджено з axios: APP_CONFIG.api.baseURL / VITE_API_URL) ── */

/**
 * База Anchor Plan для fetch/XHR з повним шляхом від origin (або абсолютний URL на той самий API, що й основний axios).
 * Раніше при відносному `/api` підставляли `https://api.{host}` — сесія Sanctum лишалась на хості SPA, а запити йшли на інший origin → user: null.
 */
function anchorPlanPathsFromAppConfig(): {
  fetchBase: string;
  axiosPrefix: string;
} {
  const apiRoot = String(APP_CONFIG.api.baseURL || "/api")
    .trim()
    .replace(/\/+$/, "");
  if (apiRoot.startsWith("http://") || apiRoot.startsWith("https://")) {
    try {
      const u = new URL(apiRoot);
      const fetchBase = `${u.origin}/api/anchor-plan`;
      return { fetchBase, axiosPrefix: fetchBase };
    } catch {
      const fb = "/api/anchor-plan";
      return { fetchBase: fb, axiosPrefix: fb };
    }
  }
  const prefix = apiRoot.startsWith("/") ? apiRoot : `/${apiRoot}`;
  const fetchBase = `${prefix}/anchor-plan`;
  return { fetchBase, axiosPrefix: "anchor-plan" };
}

/** Для `fetch(\`${base}/projects/...\`)` та легасі-скриптів — завжди коректний path/URL. */
export function getAnchorPlanApiBase(): string {
  return anchorPlanPathsFromAppConfig().fetchBase;
}

/**
 * Префікс для axios (той самий інстанс, що й решта додатку): при відносному baseURL `/api` — `anchor-plan`, інакше повний URL до `/api/anchor-plan`.
 */
export function getAnchorPlanAxiosPrefix(): string {
  return anchorPlanPathsFromAppConfig().axiosPrefix;
}

function getApiOriginForInterceptor(): string {
  const apiRoot = String(APP_CONFIG.api.baseURL || "/api")
    .trim()
    .replace(/\/+$/, "");
  if (apiRoot.startsWith("http://") || apiRoot.startsWith("https://")) {
    try {
      return new URL(apiRoot).origin;
    } catch {
      return "";
    }
  }
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return "";
}

/* ── Cookie / CSRF ──────────────────────────────────────────────── */

export function getCookie(name: string): string {
  const parts = `; ${document.cookie}`.split(`; ${name}=`);
  if (parts.length < 2) return "";
  return decodeURIComponent(parts.pop()!.split(";").shift() || "");
}

/**
 * Sanctum CSRF cookie — запит йде на той самий хост, що й API.
 */
export async function ensureSanctumCsrf(): Promise<void> {
  const apiRoot = String(APP_CONFIG.api.baseURL || "/api")
    .trim()
    .replace(/\/+$/, "");
  let url = "/sanctum/csrf-cookie";
  if (apiRoot.startsWith("http://") || apiRoot.startsWith("https://")) {
    try {
      url = `${new URL(apiRoot).origin}/sanctum/csrf-cookie`;
    } catch {
      url = "/sanctum/csrf-cookie";
    }
  }
  await fetch(url, {
    credentials: "include",
    method: "GET",
  });
}

/* ── URL resolve (через <base> або fallback) ────────────────────── */

function resolveAnchorUrl(path: string): URL {
  const rel = String(path).replace(/^\/+/, "");
  const baseHref =
    (document.querySelector("base") as HTMLBaseElement | null)?.href ||
    `${window.location.origin}/app/anchor-plan/`;
  try {
    return new URL(rel, baseHref);
  } catch {
    return new URL(rel, `${window.location.origin}/app/anchor-plan/`);
  }
}

export function anchorUiUrl(path: string): string {
  return resolveAnchorUrl(path).href;
}

/** pathname + search + hash — для history.replaceState */
export function anchorUiPath(path: string): string {
  const u = resolveAnchorUrl(path);
  return u.pathname + u.search + u.hash;
}

/**
 * Повний URL на Google OAuth з redirect після callback на поточну сторінку Anchor Plan.
 */
export function anchorGoogleLoginUrl(): string {
  if (typeof window === "undefined") {
    return authService.getLoginUrl();
  }
  const path =
    window.location.pathname + window.location.search + window.location.hash;
  return authService.getLoginUrl(path);
}

/* ── Object utils ───────────────────────────────────────────────── */

/** Плоский об'єкт з рядковими ключами (не масив); custom_links тощо. */
export function asStringKeyedRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

/* ── Fetch interceptor (Sanctum) ────────────────────────────────── */

let interceptorInstalled = false;

/**
 * Патчить window.fetch: для /api/* автоматично додає credentials,
 * Accept: application/json, X-XSRF-TOKEN при мутаціях.
 * Безпечно викликати кілька разів — встановиться один раз.
 */
function isAnchorPlanFetchUrl(url: string): boolean {
  const anchorBase = getAnchorPlanApiBase();
  return (
    url.startsWith(anchorBase) ||
    url.includes("/api/anchor-plan/") ||
    url.includes("/anchor-plan/")
  );
}

/** Bearer на Anchor Plan API ламає cookie-сесію на публічних GET — лише credentials. */
function attachBearerIfPresent(headers: Headers, requestUrl: string): void {
  if (isAnchorPlanFetchUrl(requestUrl)) {
    return;
  }
  try {
    const token = localStorage.getItem("auth_token");
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  } catch {
    /* private mode / storage blocked */
  }
}

export function setupFetchInterceptor(): void {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  const anchorBase = getAnchorPlanApiBase();
  const apiOrigin = getApiOriginForInterceptor();
  const orig = window.fetch.bind(window);

  function isApiRequest(u: string): boolean {
    if (u.startsWith(anchorBase) || u.startsWith("/api")) return true;
    if (apiOrigin && u.startsWith(apiOrigin)) return true;
    return false;
  }

  window.fetch = function (url: RequestInfo | URL, opts?: RequestInit) {
    opts = opts || {};
    const isApi = typeof url === "string" && isApiRequest(url);

    if (isApi) {
      opts.credentials = opts.credentials || "include";
      const h = new Headers(opts.headers || undefined);
      if (!h.has("Accept")) h.set("Accept", "application/json");
      // мова інтерфейсу для повідомлень бекенду (SetLocaleFromAcceptLanguage)
      if (!h.has("Accept-Language")) h.set("Accept-Language", apiLanguage());
      attachBearerIfPresent(h, typeof url === "string" ? url : String(url));
      opts.headers = h;
      const m = String(opts.method || "GET").toUpperCase();
      if (["POST", "PUT", "PATCH", "DELETE"].includes(m)) {
        return (async () => {
          await ensureSanctumCsrf();
          const t = getCookie("XSRF-TOKEN");
          if (t) (opts!.headers as Headers).set("X-XSRF-TOKEN", t);
          const csrf = document.querySelector<HTMLMetaElement>(
            'meta[name="csrf-token"]',
          )?.content;
          if (csrf) (opts!.headers as Headers).set("X-CSRF-TOKEN", csrf);
          return orig(url, opts);
        })();
      }
    }
    return orig(url, opts);
  } as typeof window.fetch;

  ensureSanctumCsrf().catch(() => {});
}

/* ── Експорт CSV (імена файлів з Content-Disposition) ───────────── */

/**
 * Витягує ім'я файлу з заголовка Content-Disposition (filename* UTF-8 або filename="...").
 */
export function parseContentDispositionFilename(
  header: string | null,
): string | null {
  if (header == null || typeof header !== "string") return null;
  const trimmed = header.trim();
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;\n]+)/i.exec(trimmed);
  if (star?.[1]) {
    try {
      let raw = star[1].trim();
      if (raw.startsWith('"') && raw.endsWith('"')) raw = raw.slice(1, -1);
      return decodeURIComponent(raw);
    } catch {
      /* ignore */
    }
  }
  const quoted = /filename="((?:[^"\\]|\\.)*)"/.exec(trimmed);
  if (quoted?.[1]) {
    return quoted[1].replace(/\\"/g, '"');
  }
  const plain = /filename=([^;\n]+)/i.exec(trimmed);
  if (plain?.[1]) {
    let v = plain[1].trim();
    if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
    return v;
  }
  return null;
}

/**
 * Fallback для a.download, узгоджено з бекендом ProjectEngineController::buildAnchorPlanExportDisplayFilename.
 */
export type AnchorPlanExportKind = "anchor-plan" | "purchases";

/** Старі виклики передавали підпис українською — зводимо до стабільного id. */
const LEGACY_EXPORT_KINDS: Record<string, AnchorPlanExportKind> = {
  "Анкор-план": "anchor-plan", // i18n-allow-cyrillic — сумісність зі старими викликами
  "Закупівлі": "purchases", // i18n-allow-cyrillic — сумісність зі старими викликами
};

export function buildAnchorPlanExportCsvFilename(
  projectName: string,
  planName: string,
  kind: AnchorPlanExportKind | string,
): string {
  const { t } = i18n.global;
  const kindId = LEGACY_EXPORT_KINDS[kind] ?? kind;
  const kindLabel =
    kindId === "anchor-plan"
      ? t("anchorPlan.components.export.kindAnchorPlan")
      : kindId === "purchases"
        ? t("anchorPlan.components.export.kindPurchases")
        : kind;
  const untitled = t("anchorPlan.components.export.untitled");
  const sanitize = (s: string, fb: string): string => {
    const t = String(s ?? "").trim();
    if (!t) return fb;
    const cleaned = t
      .replace(/[\\/:*?"<>|]/g, "-")
      .replace(/\s+/g, " ")
      .trim();
    return cleaned || fb;
  };
  const base = `${sanitize(projectName, untitled)} - ${sanitize(planName, untitled)} - ${sanitize(kindLabel, t("anchorPlan.components.export.fallbackKind"))}`;
  const truncated = base.length > 180 ? `${base.slice(0, 177)}...` : base;
  return `${truncated}.csv`;
}
